import React from 'react';
import { motion } from 'framer-motion';
import { Headphones, BookOpen, Zap, Users, TrendingUp, Shield } from 'lucide-react';

export const DigitalAssistanceSection = () => {
  const services = [
    {
      icon: Headphones,
      title: "Support Continu",
      description: "Assistance numérique proactive et réactive pour tous vos besoins technologiques. Disponible pour PME, associations et collectivités en Ardennes.",
      color: "from-blue-600 to-blue-500"
    },
    {
      icon: BookOpen,
      title: "Formation & Coaching Digital",
      description: "Montée en compétences de vos équipes avec des formations adaptées à votre contexte. Coaching personnalisé pour transformer vos processus numériques.",
      color: "from-purple-600 to-purple-500"
    },
    {
      icon: TrendingUp,
      title: "Optimisation Numérique",
      description: "Audit complet et optimisation de votre infrastructure IT. Amélioration de la productivité et de la sécurité de vos données.",
      color: "from-green-600 to-green-500"
    },
    {
      icon: Zap,
      title: "Dépannage Rapide",
      description: "Intervention rapide en cas de problème technique. Dépannage sur site ou en ligne selon vos besoins (Château-Porcien, Ardennes).",
      color: "from-yellow-600 to-yellow-500"
    },
    {
      icon: Users,
      title: "Accompagnement Projets",
      description: "Assistance complète pour vos projets numériques : refonte web, migration système, transformation digitale.",
      color: "from-pink-600 to-pink-500"
    },
    {
      icon: Shield,
      title: "Sécurité & Conformité",
      description: "Protection de vos données et conformité RGPD. Stratégie de cybersécurité adaptée à votre secteur d'activité.",
      color: "from-red-600 to-red-500"
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  return (
    <section className="py-20 px-4 md:px-6 lg:px-8 bg-gradient-to-b from-[#181C25] to-[#1a1f2b]">
      <div className="container mx-auto max-w-7xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-montserrat font-bold text-white mb-4">
            Assistance Numérique Complète
          </h2>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto">
            HTAG Facility : Votre partenaire IT et digital en Ardennes. Nous accompagnons PME, associations et collectivités dans leur transformation numérique globale.
          </p>
          <div className="mt-4 flex flex-wrap justify-center gap-3">
            <span className="px-4 py-2 bg-blue-500/10 border border-blue-500/30 rounded-full text-blue-300 text-sm font-semibold">
              Basé à Château-Porcien
            </span>
            <span className="px-4 py-2 bg-green-500/10 border border-green-500/30 rounded-full text-green-300 text-sm font-semibold">
              Assistance proactive
            </span>
            <span className="px-4 py-2 bg-purple-500/10 border border-purple-500/30 rounded-full text-purple-300 text-sm font-semibold">
              Support 360°
            </span>
          </div>
        </motion.div>

        {/* Services Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16"
        >
          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <motion.div
                key={index}
                variants={itemVariants}
                className="group relative h-full"
              >
                {/* Background glow */}
                <div className={`absolute inset-0 bg-gradient-to-br ${service.color} opacity-0 group-hover:opacity-10 rounded-2xl blur-xl transition-opacity duration-300`} />
                
                {/* Card */}
                <div className="relative bg-gradient-to-br from-gray-900 to-gray-800 border border-gray-700 group-hover:border-gray-600 rounded-2xl p-8 h-full transition-all duration-300 group-hover:shadow-xl group-hover:shadow-gray-900/50">
                  {/* Icon */}
                  <div className={`inline-flex p-4 rounded-xl bg-gradient-to-br ${service.color} mb-6`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>

                  {/* Content */}
                  <h3 className="text-xl font-montserrat font-bold text-white mb-3 group-hover:text-blue-400 transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-gray-400 text-sm leading-relaxed">
                    {service.description}
                  </p>

                  {/* Accent line */}
                  <div className={`absolute bottom-0 left-0 h-1 bg-gradient-to-r ${service.color} rounded-full w-0 group-hover:w-full transition-all duration-300`} />
                </div>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Key Benefits */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="bg-gradient-to-r from-blue-500/10 via-purple-500/10 to-pink-500/10 border border-blue-500/20 rounded-2xl p-8 md:p-12"
        >
          <h3 className="text-2xl font-montserrat font-bold text-white mb-8 text-center">
            Pourquoi choisir HTAG Facility en Ardennes ?
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { num: "01", text: "Expertise IT & Digital en Ardennes depuis plusieurs années" },
              { num: "02", text: "Interventions rapides : sur site à Château-Porcien et région" },
              { num: "03", text: "Approche personnalisée pour PME, associations, collectivités" },
              { num: "04", text: "Support continu et assistance proactive 24/7" },
              { num: "05", text: "Respect du RGPD et sécurité des données garantie" },
              { num: "06", text: "Tarifs transparents et adaptés à votre budget" }
            ].map((benefit, idx) => (
              <div key={idx} className="flex gap-4">
                <div className="text-2xl font-bold text-blue-500 font-montserrat">{benefit.num}</div>
                <p className="text-gray-300 text-sm pt-1">{benefit.text}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default DigitalAssistanceSection;
